import { Button, Checkbox, Input, Label } from '@repo/ui'
import { Check, RotateCw } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import type { ConversationCommand, ConversationQuestion } from '@/api/conversation'

type AnswerCommand = Extract<ConversationCommand, { type: 'answer' }>
const maxQuestionDocumentSelections = 32

export function ConversationQuestionControls({
  question,
  onAnswer,
  onAbandon,
  idFactory = () => crypto.randomUUID()
}: {
  question: ConversationQuestion
  onAnswer: (command: AnswerCommand) => Promise<void>
  onAbandon?: (briefId: number, runId: number) => Promise<void>
  idFactory?: () => string
}) {
  const authority = `${question.runId}:${question.suspensionKey}:${question.briefVersion}:${question.slotKey}`
  const [text, setText] = useState('')
  const [selectedIDs, setSelectedIDs] = useState<Array<number>>([])
  const [artifactIndex, setArtifactIndex] = useState<number>()
  const [pending, setPending] = useState(false)
  const [answered, setAnswered] = useState(false)
  const [error, setError] = useState<string>()
  const [validation, setValidation] = useState<string>()
  const [abandoning, setAbandoning] = useState(false)
  const retryCommand = useRef<{ command: AnswerCommand; fingerprint: string } | undefined>(undefined)
  const formRef = useRef<HTMLFormElement>(null)
  const previousAuthority = useRef(authority)
  const inFlightAnswer = useRef<{ authority: string; cancelled: boolean } | undefined>(undefined)
  const focusListenerCleanupRef = useRef<(() => void) | undefined>(undefined)
  const focusFrameRef = useRef<number | undefined>(undefined)

  useEffect(() => {
    if (previousAuthority.current === authority) return
    if (inFlightAnswer.current !== undefined) inFlightAnswer.current.cancelled = true
    focusListenerCleanupRef.current?.()
    focusListenerCleanupRef.current = undefined
    if (focusFrameRef.current !== undefined) cancelAnimationFrame(focusFrameRef.current)
    focusFrameRef.current = undefined
    previousAuthority.current = authority
    setText('')
    setSelectedIDs([])
    setArtifactIndex(undefined)
    setPending(false)
    setAnswered(false)
    setError(undefined)
    setValidation(undefined)
    retryCommand.current = undefined
  }, [authority])

  useEffect(
    () => () => {
      if (inFlightAnswer.current !== undefined) inFlightAnswer.current.cancelled = true
      focusListenerCleanupRef.current?.()
      focusListenerCleanupRef.current = undefined
      if (focusFrameRef.current !== undefined) cancelAnimationFrame(focusFrameRef.current)
      focusFrameRef.current = undefined
    },
    []
  )

  const isSingleDocument = question.kind === 'document-reference'
  // A reference question with no shortlist cannot be answered by selection, and the server
  // rejects an empty one, so submitting could only ever fail.
  const unanswerable =
    ((question.kind === 'document-reference' || question.kind === 'document-references') &&
      question.candidates.length === 0) ||
    (question.kind === 'artifact-reference' && question.artifactOptions.length === 0)
  const selectedArtifact = artifactIndex === undefined ? undefined : question.artifactOptions[artifactIndex]

  const commandForAnswer = (answer: AnswerCommand['answer']): { command: AnswerCommand; fingerprint: string } => {
    const fingerprint = JSON.stringify({ authority, answer })
    if (retryCommand.current?.fingerprint === fingerprint) return retryCommand.current
    return {
      command: {
        type: 'answer',
        clientTurnId: idFactory(),
        briefId: question.briefId,
        runId: question.runId,
        suspensionKey: question.suspensionKey,
        answer
      },
      fingerprint
    }
  }

  const commandForCurrentValue = (): { command: AnswerCommand; fingerprint: string } | undefined => {
    let answer: AnswerCommand['answer'] | undefined
    if (question.kind === 'text') {
      if (text.trim() === '') return undefined
      answer = { type: 'text', content: text.trim() }
    } else if (question.kind === 'integer') {
      if (text.trim() === '') return undefined
      const integer = Number(text)
      if (!Number.isSafeInteger(integer)) return undefined
      answer = { type: 'integer', content: integer }
    } else if (question.kind === 'document-reference' || question.kind === 'document-references') {
      if (
        selectedIDs.length === 0 ||
        selectedIDs.length > maxQuestionDocumentSelections ||
        (isSingleDocument && selectedIDs.length !== 1)
      ) {
        return undefined
      }
      answer = { type: 'documents', selectedIds: selectedIDs }
    } else if (question.kind === 'artifact-reference') {
      if (selectedArtifact === undefined) return undefined
      // The answer round-trips the candidate's type: a whole proposal names no
      // section, while a section answer keeps its exact shape.
      answer =
        selectedArtifact.type === 'proposal'
          ? {
              type: 'proposal',
              proposalId: selectedArtifact.proposalId,
              observation: selectedArtifact.observation
            }
          : {
              type: 'proposal-section',
              proposalId: selectedArtifact.proposalId,
              sectionId: selectedArtifact.sectionId,
              observation: selectedArtifact.observation
            }
    }
    if (answer === undefined) return undefined
    return commandForAnswer(answer)
  }

  const submit = async (retry?: AnswerCommand, skip = false) => {
    const prepared =
      retry === undefined
        ? skip
          ? commandForAnswer({ type: 'skip' })
          : commandForCurrentValue()
        : { command: retry, fingerprint: JSON.stringify({ authority, answer: retry.answer }) }
    if (prepared === undefined) {
      setValidation('Choose or enter a valid answer.')
      return
    }
    const { command } = prepared
    retryCommand.current = prepared
    const submittedAuthority = authority
    const attempt = { authority: submittedAuthority, cancelled: false }
    const originatedInQuestion = formRef.current?.contains(document.activeElement) ?? false
    const originForm = formRef.current
    const focusStayedInQuestionFlow = { current: true }
    const trackFocusContext = (event: Event) => {
      if (event.target instanceof Node && originForm?.contains(event.target)) return
      focusStayedInQuestionFlow.current = false
    }
    focusListenerCleanupRef.current?.()
    document.addEventListener('focusin', trackFocusContext, true)
    document.addEventListener('pointerdown', trackFocusContext, true)
    document.addEventListener('scroll', trackFocusContext, true)
    const cleanupFocusListeners = () => {
      document.removeEventListener('focusin', trackFocusContext, true)
      document.removeEventListener('pointerdown', trackFocusContext, true)
      document.removeEventListener('scroll', trackFocusContext, true)
    }
    focusListenerCleanupRef.current = cleanupFocusListeners
    inFlightAnswer.current = attempt
    setPending(true)
    setError(undefined)
    setValidation(undefined)
    try {
      await onAnswer(command)
      if (!attempt.cancelled) {
        setAnswered(true)
        retryCommand.current = undefined
      }
      if (
        !attempt.cancelled &&
        previousAuthority.current === submittedAuthority &&
        originatedInQuestion &&
        focusStayedInQuestionFlow.current
      ) {
        focusFrameRef.current = requestAnimationFrame(() => {
          focusFrameRef.current = undefined
          if (attempt.cancelled || previousAuthority.current !== submittedAuthority) return
          const active = document.activeElement
          const currentForm = formRef.current
          if (active !== document.body && active !== null && (currentForm === null || !currentForm.contains(active)))
            return
          const next =
            currentForm?.querySelector<HTMLInputElement>('input:not([disabled])') ??
            document.querySelector<HTMLInputElement>('[data-conversation-question-turn] input:not([disabled])')
          ;(next ?? document.querySelector<HTMLTextAreaElement>('#conversation-composer'))?.focus()
        })
      }
    } catch (answerError) {
      if (attempt.cancelled) return
      setError(answerError instanceof Error ? answerError.message : 'The answer was not saved.')
    } finally {
      cleanupFocusListeners()
      if (focusListenerCleanupRef.current === cleanupFocusListeners) focusListenerCleanupRef.current = undefined
      if (!attempt.cancelled) setPending(false)
      if (inFlightAnswer.current === attempt) inFlightAnswer.current = undefined
    }
  }

  const candidateLabel = useMemo(
    () => new Map(question.candidates.map((candidate) => [candidate.id, candidate.label])),
    [question.candidates]
  )

  const precheck = useMemo(() => {
    const summaries = question.satisfied.map((slot) => slot.summary).filter((summary) => summary !== '')
    if (summaries.length === 0) return undefined
    if (summaries.length === 1) return `I already have ${summaries[0]}, so I will not ask about that.`
    const last = summaries[summaries.length - 1]
    return `I already have ${summaries.slice(0, -1).join(', ')} and ${last}, so I will not ask about those.`
  }, [question.satisfied])

  return (
    <form
      ref={formRef}
      className="space-y-3"
      onSubmit={(event) => {
        event.preventDefault()
        void submit()
      }}
    >
      <fieldset disabled={pending || answered} className="space-y-3">
        <legend className="text-[0.95rem] leading-6 font-medium">{question.prompt}</legend>
        {(question.kind === 'text' || question.kind === 'integer') && (
          <div>
            <Label htmlFor={`question-${question.runId}`} className="sr-only">
              Your answer
            </Label>
            <Input
              id={`question-${question.runId}`}
              aria-label="Your answer"
              type={question.kind === 'integer' ? 'number' : 'text'}
              inputMode={question.kind === 'integer' ? 'numeric' : undefined}
              value={text}
              onChange={(event) => {
                setText(event.currentTarget.value)
                retryCommand.current = undefined
                setError(undefined)
                setValidation(undefined)
              }}
            />
          </div>
        )}
        {/*
          An empty shortlist used to render a submit button over nothing at all: no input,
          no options, and no way out, with the chat still owned by the question. Say what
          happened instead, since the answer is to add the material or drop the request.
        */}
        {(question.kind === 'document-reference' || question.kind === 'document-references') &&
          question.candidates.length === 0 && (
            <p className="text-sm text-muted-foreground">
              {question.skippable
                ? 'No presentation templates are available. You can continue without a template, or add one and try again.'
                : 'Nothing in this workspace matched this question yet. Add or connect the material you want used, then ask again, or drop this request.'}
            </p>
          )}
        {question.kind === 'artifact-reference' &&
          question.artifactOptions.length === 0 &&
          /*
            The two artifact asks empty out for different reasons, and the question
            names which one it is through its slot key: drafting wants a section to
            exist, while a deck needs every section of one proposal ready at once.
            Neither copy may recite registry keys.
          */
          (question.slotKey === 'source-proposal' ? (
            <p className="text-sm text-muted-foreground">
              Nothing in this workspace is ready to turn into a deck yet. Mark every section of the proposal you want
              ready, then ask again, or drop this request.
            </p>
          ) : (
            <p className="text-sm text-muted-foreground">
              Nothing in this workspace matched this question yet. Ask to start a proposal, or create the section you
              want drafted and ask again, or drop this request.
            </p>
          ))}
        {(question.kind === 'document-reference' || question.kind === 'document-references') && (
          <div className="flex flex-col gap-2">
            {question.candidates.map((candidate) => (
              <Label
                key={candidate.id}
                className="flex items-start gap-2 rounded-lg border px-3 py-2 font-normal hover:bg-accent"
              >
                {isSingleDocument ? (
                  <input
                    type="radio"
                    name={`question-${question.runId}-document`}
                    checked={selectedIDs[0] === candidate.id}
                    onChange={() => {
                      setSelectedIDs([candidate.id])
                      retryCommand.current = undefined
                      setError(undefined)
                      setValidation(undefined)
                    }}
                    className="mt-1 accent-primary"
                  />
                ) : (
                  <Checkbox
                    checked={selectedIDs.includes(candidate.id)}
                    disabled={
                      !selectedIDs.includes(candidate.id) && selectedIDs.length >= maxQuestionDocumentSelections
                    }
                    onCheckedChange={(checked) =>
                      setSelectedIDs((current) => {
                        retryCommand.current = undefined
                        setError(undefined)
                        setValidation(undefined)
                        if (checked === true) {
                          return current.length >= maxQuestionDocumentSelections ? current : [...current, candidate.id]
                        }
                        return current.filter((id) => id !== candidate.id)
                      })
                    }
                  />
                )}
                <span>
                  <span className="block text-sm">{candidate.label}</span>
                  {candidate.secondary && (
                    <span className="block text-xs text-muted-foreground">{candidate.secondary}</span>
                  )}
                </span>
              </Label>
            ))}
            {/*
              The count comes from the list itself rather than a second copy of the
              server's cap, which would go stale the moment the cap moved. And the way
              out is dropping the request: this form submits chosen ids only, so a
              free-text reply cannot answer a parked document question.
            */}
            {question.candidatesTruncated && (
              <p className="text-xs text-muted-foreground">
                Showing the {question.candidates.length} most recently updated documents. If the one you want is not
                listed, drop this request and name it in your next message.
              </p>
            )}
          </div>
        )}
        {question.kind === 'artifact-reference' && (
          <div className="flex flex-col gap-2">
            {question.artifactOptions.map((option, index) => (
              <Label
                key={`${option.type}:${option.proposalId}:${option.sectionId}`}
                className="flex items-start gap-2 rounded-lg border px-3 py-2 font-normal hover:bg-accent"
              >
                <input
                  type="radio"
                  name={`question-${question.runId}-artifact`}
                  checked={artifactIndex === index}
                  onChange={() => {
                    setArtifactIndex(index)
                    retryCommand.current = undefined
                    setError(undefined)
                    setValidation(undefined)
                  }}
                  className="mt-1 accent-primary"
                />
                <span>
                  <span className="block text-sm">{option.label}</span>
                  <span className="block text-xs text-muted-foreground">{option.secondary}</span>
                </span>
              </Label>
            ))}
          </div>
        )}
        <div className="flex flex-wrap items-center gap-2">
          <Button type="submit" size="sm" disabled={pending || answered || unanswerable}>
            {pending
              ? 'Saving answer'
              : answered
                ? 'Answer saved'
                : question.skippable
                  ? 'Use selected template'
                  : 'Answer question'}
          </Button>
          {question.skippable && (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              disabled={pending || answered}
              onClick={() => void submit(undefined, true)}
            >
              Continue without a template
            </Button>
          )}
          {/*
            A parked question owns the chat until it is answered: it counts as active work,
            so the chat cannot even be archived. Dropping the request leaves the brief
            behind to start again from, which is why this reads as abandoning the task
            rather than dismissing a notification.
          */}
          {onAbandon !== undefined && (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              disabled={pending || answered || abandoning}
              onClick={() => {
                setAbandoning(true)
                setError(undefined)
                void onAbandon(question.briefId, question.runId)
                  .catch(() => setError('That did not go through. Try again.'))
                  .finally(() => setAbandoning(false))
              }}
            >
              {abandoning ? 'Dropping' : 'Drop this request'}
            </Button>
          )}
        </div>
      </fieldset>
      {/*
        The design authority puts this line after the question, not before it: the ask
        comes first and the reassurance follows. It reads back the summaries the user
        gave, never the registry labels and never the keys, because its whole job is to
        show the engine is not asking again for something already said.
      */}
      {precheck !== undefined && (
        <p className="flex items-start gap-2 text-xs text-muted-foreground">
          <Check className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
          <span>{precheck}</span>
        </p>
      )}
      {selectedIDs.length > 0 && question.kind === 'document-references' && (
        <p className="text-xs text-muted-foreground">
          Selected: {selectedIDs.map((id) => candidateLabel.get(id)).join(', ')}
        </p>
      )}
      {validation && <p className="text-sm text-destructive">{validation}</p>}
      {error && (
        <div className="flex items-center gap-3 text-sm text-destructive" role="alert">
          <span>{error}</span>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => retryCommand.current && void submit(retryCommand.current.command)}
          >
            <RotateCw className="size-4" />
            Try answer again
          </Button>
        </div>
      )}
    </form>
  )
}
